import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaArrowLeft } from 'react-icons/fa';
import api from '../../../api/apiClient';

const InstructorAssessmentAnalytics = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [analytics, setAnalytics] = useState(null);
  const [results, setResults] = useState([]);
  const [enrolledUsers, setEnrolledUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.assessments.getAdminResults(id);
        setAnalytics(response.data?.data?.analytics || null);
        setResults(response.data?.data?.results || []);
        setEnrolledUsers(response.data?.data?.enrolled_users || []);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const getUserDisplayName = (person) => {
    if (!person) return '-';
    return person.username || person.email || `${person.first_name || ''} ${person.last_name || ''}`.trim() || '-';
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner animation="border" className="text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-4 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <div className="flex items-center gap-4 mb-6">
        <button
          type="button"
          onClick={() => navigate('/instructor/assessments')}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg shadow-md hover:bg-purple-50 text-sm font-medium"
        >
          <FaArrowLeft className="w-4 h-4" /> Back
        </button>
        <h5 className="mb-0 text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
          Assessment Analytics
        </h5>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div className="bg-white rounded-xl shadow p-4">
          <div className="text-sm text-gray-500">Enrolled</div>
          <div className="text-2xl font-bold">{analytics?.enrolled_count || enrolledUsers.length || 0}</div>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <div className="text-sm text-gray-500">Attempted</div>
          <div className="text-2xl font-bold">{analytics?.attempted_count || results.length || 0}</div>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <div className="text-sm text-gray-500">Average Score</div>
          <div className="text-2xl font-bold">{analytics?.average_score || 0}%</div>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <div className="text-sm text-gray-500">Pass Percentage</div>
          <div className="text-2xl font-bold">{analytics?.pass_percentage || 0}%</div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-4 mb-4">
        <h6 className="mb-3 font-bold">Enrolled Users &amp; Progress</h6>
        <div className="overflow-x-auto">
          <table className="table table-bordered mb-0 text-sm">
            <thead className="bg-purple-50">
              <tr>
                <th>User</th>
                <th>Schedule</th>
                <th>Progress</th>
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              {enrolledUsers.length === 0 ? (
                <tr><td colSpan={4} className="text-center text-muted">No enrolled users.</td></tr>
              ) : (
                enrolledUsers.map((item) => {
                  const result = results.find((row) => Number(row.user_id || row.user?.id) === Number(item.user_id));
                  return (
                    <tr key={item.id}>
                      <td>{getUserDisplayName(item.user)}</td>
                      <td className="text-xs text-gray-600">
                        {item.available_from ? new Date(item.available_from).toLocaleDateString() : '-'}
                        {' → '}
                        {item.available_to ? new Date(item.available_to).toLocaleDateString() : '-'}
                      </td>
                      <td>
                        <Badge bg={result ? (result.result_status === 'pass' ? 'success' : 'warning') : 'secondary'}>
                          {result ? result.result_status : 'Not attempted'}
                        </Badge>
                      </td>
                      <td>{result ? `${result.percentage}%` : '-'}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-4">
        <h6 className="mb-3 font-bold">Attempt Results</h6>
        <div className="overflow-x-auto">
          <table className="table table-bordered mb-0 text-sm">
            <thead className="bg-purple-50">
              <tr>
                <th>User</th>
                <th>Obtained</th>
                <th>Total</th>
                <th>Percentage</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {results.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-muted">No attempts yet.</td></tr>
              ) : (
                results.map((item) => (
                  <tr key={item.id}>
                    <td>{item.user ? `${item.user.first_name || ''} ${item.user.last_name || ''}`.trim() || item.user.email : '-'}</td>
                    <td>{item.obtained_marks}</td>
                    <td>{item.total_marks}</td>
                    <td>{item.percentage}%</td>
                    <td>{item.result_status}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InstructorAssessmentAnalytics;
